/**
 * ── A ORDEM DAS SEÇÕES DA VITRINE, E O TOM DE CADA UMA (leva A2 da bancada) ──
 *
 * A vitrine do aluno desenhava as seções numa ordem fixa no JSX: herói,
 * saudação, «continue de onde parou», as prateleiras por `position`, trilhas.
 * O dono quer dispor as seções, escondê-las e escurecer/clarear regiões — e o
 * editor visual precisa gravar isso em algum lugar que o aluno leia.
 *
 * A seção `trilhas` **saiu da ordem na `0467`** — a trilha virou produto e tem
 * prateleira própria. Ver o bloco de `ORDEM_PADRAO` para as três remoções e por
 * que o literal continua no enum.
 *
 * Este arquivo NÃO importa nada, de propósito: ele roda também no navegador do
 * aluno (a prévia e os testes montam `<Vitrine>` com um corpo que pode não
 * trazer `secoes`), e o aluno não paga zod. A validação do que se grava mora em
 * `vitrine-settings.ts` (`layoutDaVitrine`), que lê as constantes daqui.
 *
 * ## As regras, que são poucas e são todas tolerantes
 *
 * - `secoes` gravado VAZIO = «não mexi»: a ordem de hoje, tom `fundo` em tudo.
 * - Prateleira listada no layout que já não existe → some (ponteiro morto não
 *   quebra; é o precedente de `heroi.curso`).
 * - Prateleira que existe e NÃO está listada → entra no FIM, na ordem de
 *   `position` — a prateleira nova aparece sem ninguém a arrastar. (Era «antes
 *   de `trilhas`»; a seção saiu da ordem na `0467`.)
 * - Seção fixa não listada → entra na posição padrão relativa.
 * - `oculta` → some. Seção repetida → a primeira vale, as outras são ignoradas.
 * - Só sai o que está PRESENTE: sem retomada para quem não começou nada, sem
 *   herói quando ele está desligado. E uma entrada `{"tipo":"trilhas"}` ainda
 *   gravada é saltada por aqui, porque ninguém mais a declara presente.
 */

export const TIPOS_DE_SECAO = ['heroi', 'saudacao', 'retomada', 'prateleira', 'trilhas'] as const;
export type TipoDeSecao = (typeof TIPOS_DE_SECAO)[number];

/** Os três tons derivados do tema — ver `--tom-*` em `apps/aluno/src/lib/tema.ts`. */
export const TONS = ['fundo', 'superficie', 'claro'] as const;
export type Tom = (typeof TONS)[number];

export type SecaoFixa = Exclude<TipoDeSecao, 'prateleira'>;

/**
 * As seções fixas que a vitrine ainda DESENHA. `trilhas` saiu na `0467`.
 *
 * O literal continua em `TIPOS_DE_SECAO` (ver o bloco de `ORDEM_PADRAO` para a
 * razão), logo `SecaoFixa` continua a incluí-lo — é com ele que uma entrada
 * gravada se lê e se salta. Este tipo é o outro lado: **quem declara o que está
 * presente já não tem como declarar `trilhas`**, e os quatro chamadores de
 * `secoesDaVitrine` foram cobrados pelo compilador em vez de por um `grep`.
 */
export type SecaoFixaViva = Exclude<SecaoFixa, 'trilhas'>;

/** O que fica gravado em `settings.layout.secoes[]`. */
export interface SecaoGravada {
  tipo: TipoDeSecao;
  /** Só para `prateleira`: o `area_shelf.id`, ou `todos` para a sintética. */
  id?: string;
  oculta?: boolean;
  tom?: Tom;
}

/** O que a tela desenha, na ordem. */
export interface SecaoDaVitrine {
  tipo: TipoDeSecao;
  id: string | null;
  tom: Tom;
}

/*
 * ── A SEÇÃO `trilhas` SAI DA ORDEM, E O LITERAL FICA NO ENUM (`0467`) ───────
 *
 * Ordem do dono, 06/10/2026: *«na vitrine sai o espaço especial de trilhas já
 * que ela terá sua própria prateleira ou prateleiras definidas pelo tutor,
 * inclusive se misturando com cursos»*. A trilha passou a ser produto; o quarto
 * escondido no fim da vitrine era o que ela deixou de ser.
 *
 * **As três remoções, e cada uma fecha um caminho de volta:**
 *
 * 1. `'trilhas'` sai de `ORDEM_PADRAO` — e é esta que importa. O reencaixe das
 *    seções não listadas percorre `ORDEM_PADRAO`, logo **o que não está lá não
 *    pode voltar**. Sem isto, uma área cujo layout gravado não mencione
 *    `trilhas` recebia a seção de volta na próxima visita, e a trilha apareceria
 *    DUAS vezes: na prateleira nova e no quarto antigo;
 * 2. sai do ponto de inserção das prateleiras faltantes (abaixo) — que
 *    **simplifica** a função: elas vão sempre para o fim;
 * 3. `presentes.trilhas` sai de quem monta o `Record` (`aluno.ts`) — e aí uma
 *    entrada `{"tipo":"trilhas"}` ainda gravada é **saltada em silêncio** pelo
 *    `if (!presentes[g.tipo])` do laço, sem ninguém ter de limpar o JSON.
 *
 * ## O literal CONTINUA em `TIPOS_DE_SECAO`, e isso é decisão
 *
 * Tirá-lo do enum faria `layoutDaVitrine` (o zod) recusar toda entrada
 * `{"tipo":"trilhas"}` gravada — e `lerSalvandoOQueVale` responderia tirando o
 * ELEMENTO do array, o que é o comportamento certo mas é uma escrita a mais
 * sobre o `settings` de quem nunca pediu nada. Mantê-lo custa zero: o literal
 * não está em ordem nenhuma, e a entrada gravada é saltada pelo `presentes`.
 *
 * A DML da `0469` serve então um propósito só: pôr a prateleira nova **no lugar
 * e com o tom** que a seção tinha. Perder o `tom` reporia a faixa em `fundo` em
 * silêncio.
 */
/** A ordem de hoje — a que vale quando ninguém mexeu. */
export const ORDEM_PADRAO: readonly SecaoFixaViva[] = ['heroi', 'saudacao', 'retomada'];

/**
 * Onde as prateleiras entram na ordem padrão: depois de `retomada`, no fim.
 *
 * Eram «entre `retomada` e `trilhas`». Com a seção fora da ordem, **todas as
 * fixas estão antes das prateleiras** — e a lista continua aqui, nomeada, em vez
 * de virar «é igual a `ORDEM_PADRAO`»: o dia em que uma seção fixa nascer para
 * ir abaixo das prateleiras, é esta linha que decide, e ela tem de existir para
 * poder divergir.
 */
const ANTES_DAS_PRATELEIRAS: readonly SecaoFixaViva[] = ['heroi', 'saudacao', 'retomada'];

export function secoesDaVitrine(
  gravadas: readonly SecaoGravada[],
  prateleiras: readonly { id: string }[],
  presentes: Record<SecaoFixaViva, boolean>,
): SecaoDaVitrine[] {
  const existentes = new Set(prateleiras.map((p) => p.id));
  const saida: SecaoDaVitrine[] = [];
  const vistas = new Set<string>();
  const chave = (s: { tipo: TipoDeSecao; id?: string | null }) =>
    s.tipo === 'prateleira' ? `prateleira:${s.id ?? ''}` : s.tipo;
  const ocultas = new Set<string>();

  for (const g of gravadas) {
    const k = chave(g);
    if (vistas.has(k)) continue;
    vistas.add(k);
    if (g.oculta) {
      ocultas.add(k);
      continue;
    }
    if (g.tipo === 'prateleira') {
      if (!g.id || !existentes.has(g.id)) continue;
      saida.push({ tipo: 'prateleira', id: g.id, tom: g.tom ?? 'fundo' });
      continue;
    }
    /*
     * A entrada `{"tipo":"trilhas"}` ainda gravada em `settings` é saltada aqui,
     * **em silêncio e de propósito** (`0467`): ninguém mais a declara presente, e
     * limpá-la do JSON de toda área seria uma escrita sobre quem não pediu nada.
     * Este ramo é explícito porque o `Record` já não tem a chave — sem ele o
     * `presentes[g.tipo]` seria erro de tipo, e é bom que seja.
     */
    if (g.tipo === 'trilhas') continue;
    if (!presentes[g.tipo]) continue;
    saida.push({ tipo: g.tipo, id: null, tom: g.tom ?? 'fundo' });
  }

  /*
   * As prateleiras que existem e ninguém listou vão para o FIM, na ordem de
   * `position` em que chegaram.
   *
   * Eram inseridas «antes de `trilhas`, se ela estiver na lista». Com a seção
   * fora da ordem (ver o bloco de `ORDEM_PADRAO`) não há mais âncora, e o
   * `splice` saiu: **a prateleira nova aparece no fim sem ninguém a arrastar**,
   * que é o que ela já fazia em toda área que não listava `trilhas`.
   */
  const faltantes = prateleiras
    .filter((p) => !vistas.has(`prateleira:${p.id}`))
    .map((p): SecaoDaVitrine => ({ tipo: 'prateleira', id: p.id, tom: 'fundo' }));
  saida.push(...faltantes);

  /* As seções fixas que existem e ninguém listou entram na posição padrão
   * relativa: as de cima do padrão vão para o topo (na ordem do padrão), as de
   * baixo para o fim. Uma seção oculta de propósito NÃO volta por aqui. */
  const cabeca: SecaoDaVitrine[] = [];
  const cauda: SecaoDaVitrine[] = [];
  for (const tipo of ORDEM_PADRAO) {
    if (vistas.has(tipo) || ocultas.has(tipo) || !presentes[tipo]) continue;
    (ANTES_DAS_PRATELEIRAS.includes(tipo) ? cabeca : cauda).push({ tipo, id: null, tom: 'fundo' });
  }
  return [...cabeca, ...saida, ...cauda];
}

/**
 * ── AS PRATELEIRAS, NA ORDEM DA VITRINE, PARA QUEM SÓ DESENHA PRATELEIRAS ───
 *
 * **O defeito que esta função fecha, medido em 28/09/2026.** O tutor arrastou
 * «Elevando o Nível» para o segundo lugar da vitrine. A web obedeceu; o app do
 * telemóvel continuou a desenhar a faixa em último. Os dois números, lidos no
 * banco de produção:
 *
 * | prateleira | lugar na vitrine (`settings.layout.secoes`) | `area_shelf.position` |
 * |---|---|---|
 * | AutoNext | 1.º | 0 |
 * | **Elevando o Nível** | **2.º** | **4** |
 * | Vibe Stack | 3.º | 1 |
 * | Automações | 4.º | 2 |
 * | Ferramentas | 5.º | 3 |
 *
 * A coluna `position` é a ordem em que as prateleiras foram CRIADAS, e era a
 * única coisa que o app lia — daí o sintoma exato do dono: *«acabou colocando
 * em ordem de criação»*. **Arrastar na vitrine nunca escreveu em `position`**,
 * e não devia mesmo escrever: a lista de seções mistura prateleiras com herói,
 * saudação, retomada e trilhas, e uma coluna de inteiros numa tabela de
 * prateleiras não sabe dizer que o herói ficou entre duas delas.
 *
 * ## Por que isto não é uma segunda régua
 *
 * Porque **é a mesma**: esta função chama `secoesDaVitrine` e fica com a
 * subsequência das prateleiras. Uma ordenação escrita à parte — «ordena por
 * `secoes`, e o que não estiver lá vai ao fim» — seria a segunda cópia da
 * regra, e a primeira divergência entre as duas telas voltaria pela porta da
 * prateleira nova, da oculta ou do ponteiro morto, que são exactamente os três
 * casos que aquela função já sabe tratar.
 *
 * ## Por que `trilhas` é a única seção fixa que este chamador precisa de saber
 *
 * **Descoberto pela própria cerca, e a primeira versão desta função estava
 * errada por não o saber.** Herói, saudação e retomada entram e saem da lista
 * sem mexer numa prateleira: elas não são prateleiras, e ao filtrar a saída
 * desaparecem. `trilhas` é diferente porque é o PONTO DE INSERÇÃO das
 * prateleiras que ninguém listou — e ela pode estar gravada no meio das
 * listadas. Com `trilhas` na tela, uma prateleira nova entra antes dela (logo,
 * possivelmente no meio das outras); sem ela, vai para o fim. Passar `false`
 * onde a web tem trilhas punha a faixa nova noutro lugar, e era a mesma classe
 * de defeito que esta função existe para fechar.
 *
 * As outras três vão em `false` e isso não muda nada — a cerca
 * `a-ordem-das-prateleiras-e-a-mesma-nos-dois-lados` prova a igualdade com a
 * web nas dezasseis combinações de `presentes`, e o que ela mede é que só
 * `trilhas` conta.
 *
 * E a prateleira OCULTA some aqui também — é o mesmo fato do tutor, e um app
 * que mostrasse a faixa que a web esconde estaria a contradizer a tela dele.
 */
export function prateleirasNaOrdemDaVitrine<T extends { id: string }>(
  gravadas: readonly SecaoGravada[],
  prateleiras: readonly T[],
): T[] {
  const porId = new Map(prateleiras.map((p) => [p.id, p]));
  /*
   * O terceiro parâmetro era `{ trilhas: boolean }` e **saiu** (`0467`): ele
   * existia só para dizer se a seção `trilhas` ancorava a inserção das
   * prateleiras não listadas. Com a seção fora da ordem, elas vão para o fim e a
   * resposta não depende de mais nada — um parâmetro que já não muda a resposta é
   * um parâmetro que o próximo chamador preenche por adivinhação.
   */
  return secoesDaVitrine(gravadas, prateleiras, {
    heroi: false,
    saudacao: false,
    retomada: false,
  }).flatMap((s) => {
    if (s.tipo !== 'prateleira' || !s.id) return [];
    const p = porId.get(s.id);
    return p ? [p] : [];
  });
}
